import { createEffect, createEvent, createStore, sample } from "effector";
import type {
  AssistantAction,
  AssistantMessage,
  AssistantMarkdownMessage,
  AssistantMessageBlock,
  AssistantMessageLink,
  AssistantSuggestion,
} from "../types/assistant";
import type { Photo, Sight, SightId, Video } from "../types";
import type { SceneMarker } from "../shared/sceneContext";
import { publishEventBusEvent } from "../services/assistantClient";
import type {
  EventBusContext,
  EventBusEvent,
  EventBusMessage,
  EventBusRequest,
  AssistantMessageEvent,
  ThinkingEvent,
  CancelGenerationEvent,
  SuggestionClickEvent,
} from "../shared/eventBus";
import {
  $assistantSessionId,
  assistantStreamMessageReceived,
  assistantStreamSessionReceived,
} from "./assistantSession";
import {
  $pendingSceneOpenRequests,
  $bufferedSceneOpenMessage,
  $assistantWaitingSceneOpen,
  sceneOpenResolved,
  sceneOpenMessageBuffered,
  sceneOpenMessageFlushed,
  sceneOpenIntroReceived,
} from "./sceneOpen";
import { sceneLoadingFinished, $sceneLoadingState } from "./sceneLoading";
import {
  $appState,
  $sightsStore,
  $currentSight,
  goToSight,
  userSelectedSight,
  userInteracted,
} from "./appState";
import type { SightSelectionPayload } from "./appState";
import { updateAudioConfig } from "./appConfig";
import { $locale } from "./locale";
import { formatMessage, getMessages } from "../i18n";
import type { Locale } from "../shared/locale";
import { openPoiArtifact } from "./sceneNavigation";
import { tourReceived } from "./visitedSights";

type SceneContextOptions = {
  currentSight: Sight | null;
  markers: SceneMarker[];
};

type EventBusContextOptions = SceneContextOptions & {
  locale: Locale;
  sceneId: string | number | null;
};

const buildEventBusContext = ({
  currentSight,
  markers,
  locale,
  sceneId,
}: EventBusContextOptions): EventBusContext => {
  const markerDetails = markers.map((marker) => ({
    sightId: marker.sightId,
    visible: marker.visible,
    occluded: marker.occluded,
    distance: Number.isFinite(marker.distance)
      ? Number(marker.distance)
      : null,
  }));

  return {
    locale,
    sceneId: sceneId ?? "main",
    currentSight: currentSight?.id ?? null,
    markers: markerDetails,
  };
};

type EventBusMessageWithEvent<T extends EventBusEvent["type"]> = EventBusMessage & {
  event: Extract<EventBusEvent, { type: T }>;
};

const isThinkingMessage = (
  message: EventBusMessage
): message is EventBusMessageWithEvent<ThinkingEvent["type"]> =>
  message.event.type === "thinking";

const isAssistantMessage = (
  message: EventBusMessage
): message is EventBusMessageWithEvent<AssistantMessageEvent["type"]> =>
  message.event.type === "assistant_message";

export type AssistantHudStatus =
  | "idle"
  | "sending"
  | "awaiting"
  | "rendering"
  | "error";

export type AssistantHudMessage =
  | {
      id: string;
      role: "assistant";
      blocks: AssistantMessageBlock[];
      suggestions: AssistantSuggestion[];
      links: AssistantMessageLink[];
      actions: AssistantAction[];
      disableAnimation?: boolean;
      requestId?: string | null;
    }
  | {
      id: string;
      role: "user";
      text: string;
      requestId?: string | null;
    };

const createMessageId = (role: AssistantHudMessage["role"]) =>
  `${role}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const createRequestId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `req-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const isMatchingRequestId = (
  latestRequestId: string | null,
  requestId?: string | null
) => {
  if (!latestRequestId) {
    return true;
  }
  const normalized = requestId?.trim() ?? "";
  return normalized !== "" && normalized === latestRequestId;
};

/**
 * True when this assistant_message is a reply to an outstanding scene_open
 * POST. We branch incoming messages on this flag — scene_open responses go
 * through the dedicated handler (`sceneOpenResponseReceived`) which:
 *   - extracts tour with sceneId from the SEND-time snapshot (not current
 *     state — race protection),
 *   - drops the card silently if `content === ""` (no authored intro),
 *   - buffers the card until `sceneLoadingFinished` if the scene is still
 *     loading (so typewriter doesn't run behind the preloader),
 *   - applies actions through the existing `assistantActionsReceived` event,
 *   - resolves the waiting gate (`sceneOpenResolved`).
 */
const isSceneOpenResponse = (
  pendingSceneOpen: Map<string, string>,
  msgRequestId?: string | null,
): boolean => {
  if (!msgRequestId) return false;
  return pendingSceneOpen.has(msgRequestId);
};

const createUserMessage = (text: string, requestId?: string | null): AssistantHudMessage => ({
  id: createMessageId("user"),
  role: "user",
  text,
  requestId,
});

const toParagraphBlocks = (text: string): AssistantMessageBlock[] => [
  {
    type: "paragraph",
    text,
  },
];

type AnyAssistantMessage = AssistantMessage | AssistantMarkdownMessage;

const isMarkdownMessage = (
  message: AnyAssistantMessage
): message is AssistantMarkdownMessage =>
  "content" in message && typeof message.content === "string";

const normalizeMessageBlocks = (message: AnyAssistantMessage): AssistantMessageBlock[] => {
  if (isMarkdownMessage(message)) {
    return [{ type: "markdown", content: message.content, images: message.images ?? [] }];
  }
  return message.blocks ?? [];
};

/**
 * Backend contract for scene_open: `content === ""` (markdown) — or
 * empty blocks array — means "no authored card for this scene". We must
 * NOT render an empty chat bubble in that case, but the message may still
 * carry tour[] and/or actions which we WILL apply.
 *
 * For markdown we also treat a card as empty when content is whitespace
 * AND no images are attached (a content-less markdown card with an image
 * is rare but valid — we render it).
 */
const isEmptyAssistantPayload = (payload: AnyAssistantMessage): boolean => {
  if (isMarkdownMessage(payload)) {
    const text = payload.content?.trim() ?? "";
    const hasImages = (payload.images?.length ?? 0) > 0;
    return text === "" && !hasImages;
  }
  return (payload.blocks?.length ?? 0) === 0;
};

const createAssistantMessage = (
  blocks: AssistantMessageBlock[],
  suggestions: AssistantSuggestion[],
  actions: AssistantAction[],
  links: AssistantMessageLink[] = [],
  disableAnimation?: boolean,
  requestId?: string | null
): AssistantHudMessage => ({
  id: createMessageId("assistant"),
  role: "assistant",
  blocks,
  suggestions,
  links,
  actions,
  disableAnimation,
  requestId,
});

const $latestAnimatedAssistantMessageId = createStore<string | null>(null);

import { assistantChatReset } from './assistantEvents';
import { $sdkSceneMarkersGetter, sceneSnapshotConsumed } from './sceneState';

export { assistantChatReset };

// `.on(sceneSnapshotConsumed, …)` восстанавливает чат при возврате из
// inside-сцены. sceneSnapshotConsumed fires в одной транзакции с
// `assistantChatReset` (clock — тот же `setSceneId`); reducer `.reset`
// гарантированно отрабатывает раньше, поэтому пустой массив сразу
// перетирается восстановленными сообщениями. Сообщения в snapshot уже
// помечены `disableAnimation: true` на стороне `AssistantHud.handleEnterScene`,
// иначе typewriter перезапустился бы при возврате.
export const $assistantMessages = createStore<AssistantHudMessage[]>([])
  .reset(assistantChatReset)
  .on(sceneSnapshotConsumed, (_, snapshot) => snapshot.messages);

export const $assistantStatus = createStore<AssistantHudStatus>("idle")
  .reset(assistantChatReset);
export const $assistantIsBusy = createStore(false)
  .reset(assistantChatReset);
export const $assistantThinkingText = createStore<string | null>(null)
  .reset(assistantChatReset);
// NOTE: `$assistantSessionId` lives in `assistantSession.ts` and is
// intentionally NOT reset on `assistantChatReset` — see that module's
// header comment for the rationale (SSE binding stability).
const $latestRequestId = createStore<string | null>(null)
  .reset(assistantChatReset);

const $pendingRequestIds = createStore<Set<string>>(new Set())
  .reset(assistantChatReset);

// Re-export so existing consumers (e.g. `AssistantHud`) and new code
// (chat-input disabled state) can read the gate without depending on the
// `sceneOpen` module directly.
export { $assistantWaitingSceneOpen } from "./sceneOpen";

export const assistantPromptSubmitted = createEvent<string>();
export const assistantPromptSubmittedSilently = createEvent<string>();
export const assistantPoiClickRequested = createEvent<{ id: SightId }>();
export const assistantExternalMessageRequested = createEvent<{
  text: string;
  status?: AssistantHudStatus;
  suggestions?: AssistantSuggestion[];
  actions?: AssistantAction[];
  disableAnimation?: boolean;
}>();
export const assistantStopRequested = createEvent();
export const assistantAnimationSkipRequested = createEvent();
export const assistantSkipAllAnimations = createEvent();
const assistantUserMessageRequested = createEvent<string>();
export const assistantPromptAccepted = createEvent<{ text: string; requestId: string }>();
const assistantAwaiting = createEvent();
const assistantThinkingTextReceived = createEvent<string | null>();
// NOTE: `assistantStreamSessionReceived` and `assistantStreamMessageReceived`
// are imported from `assistantSession.ts` — see top-of-file imports.
const assistantMessageAdded = createEvent<{
  message: AssistantHudMessage;
  status: AssistantHudStatus;
}>();
export const assistantTextAnimationCompleted = createEvent<string>();
export const assistantSuggestionSelected = createEvent<AssistantSuggestion>();
const assistantActionsReceived = createEvent<AssistantAction[]>();
export type AssistantViewerSource = 'inline-message' | 'gallery-grid' | 'banner';
export const assistantViewerOpened = createEvent<{
  items: Array<Photo | Video>;
  index?: number;
  source?: AssistantViewerSource;
}>();
export const assistantViewerClosed = createEvent();
export const assistantViewerIndexChanged = createEvent<number>();

$assistantThinkingText.on(assistantThinkingTextReceived, (_, text) => text);

$assistantThinkingText.reset(assistantPoiClickRequested);

export const $assistantViewerItems = createStore<Array<Photo | Video>>([])
  .on(assistantViewerOpened, (_, { items }) => items)
  .reset(assistantViewerClosed);

export const $assistantViewerOpen = createStore(false)
  .on(assistantViewerOpened, () => true)
  .on(assistantViewerClosed, () => false);

export const $assistantViewerIndex = createStore(0)
  .on(assistantViewerOpened, (_, payload) => payload.index ?? 0)
  .on(assistantViewerIndexChanged, (_, index) => index)
  .reset(assistantViewerClosed);

export const publishEventBusFx = createEffect<EventBusRequest, { sessionId: string }>(
  async (payload) => publishEventBusEvent(payload)
);

const cancelGenerationFx = createEffect<
  { requestIds: string[]; sessionId: string | null },
  void
>(async ({ requestIds, sessionId }) => {
  await Promise.allSettled(
    requestIds.map((requestId) =>
      publishEventBusEvent({
        sessionId: sessionId ?? undefined,
        event: {
          type: "cancel_generation",
          requestId,
        } satisfies CancelGenerationEvent,
      })
    )
  );
});

// Waiting-gate: while a scene_open is in flight (intro card not yet
// arrived), drop all user-initiated submits. The UI also disables
// ChatInput in that state, so this is a safety net for programmatic
// emitters and for the race between scene_open POST and an enthusiastic
// user-press on a stale (cached HTML) keystroke.

sample({
  clock: assistantPromptSubmitted,
  source: { isBusy: $assistantIsBusy, waitingSceneOpen: $assistantWaitingSceneOpen },
  filter: ({ isBusy, waitingSceneOpen }, prompt) =>
    !waitingSceneOpen && isBusy && Boolean(prompt.trim()),
  fn: () => undefined,
  target: assistantStopRequested,
});

sample({
  clock: assistantPromptSubmitted,
  source: $assistantWaitingSceneOpen,
  filter: (waitingSceneOpen, prompt) =>
    !waitingSceneOpen && Boolean(prompt.trim()),
  fn: (_, prompt) => ({
    text: prompt.trim(),
    requestId: createRequestId(),
  }),
  target: assistantPromptAccepted,
});

sample({
  clock: assistantPromptSubmittedSilently,
  source: { isBusy: $assistantIsBusy, waitingSceneOpen: $assistantWaitingSceneOpen },
  filter: ({ isBusy, waitingSceneOpen }, prompt) =>
    !waitingSceneOpen && isBusy && Boolean(prompt.trim()),
  fn: () => undefined,
  target: assistantStopRequested,
});

sample({
  clock: assistantPromptSubmittedSilently,
  source: $assistantWaitingSceneOpen,
  filter: (waitingSceneOpen, prompt) =>
    !waitingSceneOpen && Boolean(prompt.trim()),
  fn: (_, prompt) => ({
    text: prompt.trim(),
    requestId: createRequestId(),
  }),
  target: assistantPromptAccepted,
});

sample({
  clock: assistantPromptSubmitted,
  target: userInteracted,
});

$assistantMessages.on(assistantPromptAccepted, (messages, payload) => [
  ...messages,
  createUserMessage(payload.text, payload.requestId),
]);

$assistantMessages.on(assistantUserMessageRequested, (messages, text) => [
  ...messages,
  createUserMessage(text),
]);

sample({
  clock: assistantExternalMessageRequested,
  fn: ({ text, status, suggestions, actions, disableAnimation }) => ({
    message: createAssistantMessage(
      toParagraphBlocks(text),
      suggestions ?? [],
      actions ?? [],
      [],
      disableAnimation
    ),
    status: status ?? "rendering",
  }),
  target: assistantMessageAdded,
});

$assistantStatus.on(assistantPromptAccepted, () => "sending");
$assistantIsBusy.on(assistantPromptAccepted, () => true);
$assistantThinkingText.reset(assistantPromptAccepted);
$latestRequestId.on(assistantPromptAccepted, (_, payload) => payload.requestId);

$pendingRequestIds.on(assistantPromptAccepted, (ids, payload) => {
  const next = new Set(ids);
  next.add(payload.requestId);
  return next;
});

$assistantStatus.on(assistantAwaiting, () => "awaiting");


// Thinking events: filter out scene_open responses. Per backend contract
// scene_open SHOULD NOT emit thinking, but in practice we've observed
// the server sending one ("Stepping back in time...") — without this
// guard it leaks into `$assistantStatus = "awaiting"` + thinking text
// overlay, even though scene_open never runs OpenAI and the chat is
// blocked behind `$assistantWaitingSceneOpen`.
sample({
  clock: assistantStreamMessageReceived,
  source: {
    latestRequestId: $latestRequestId,
    pendingSceneOpen: $pendingSceneOpenRequests,
  },
  filter: ({ latestRequestId, pendingSceneOpen }, message) =>
    isThinkingMessage(message) &&
    !isSceneOpenResponse(pendingSceneOpen, message.event.requestId) &&
    isMatchingRequestId(latestRequestId, message.event.requestId),
  target: assistantAwaiting,
});

sample({
  clock: assistantStreamMessageReceived,
  source: {
    latestRequestId: $latestRequestId,
    pendingSceneOpen: $pendingSceneOpenRequests,
  },
  filter: ({ latestRequestId, pendingSceneOpen }, message) =>
    isThinkingMessage(message) &&
    !isSceneOpenResponse(pendingSceneOpen, message.event.requestId) &&
    isMatchingRequestId(latestRequestId, message.event.requestId),
  fn: (_source, message) => {
    const event = message.event;
    if (event.type !== "thinking") {
      throw new Error("Invariant: expected thinking event.");
    }
    const text = event.text?.trim();
    return text ? text : null;
  },
  target: assistantThinkingTextReceived,
});

// Regular assistant_message handler (user_message / poi_click /
// suggestion_click responses). scene_open responses are EXCLUDED here —
// they go through the dedicated handler below.
sample({
  clock: assistantStreamMessageReceived,
  source: {
    latestRequestId: $latestRequestId,
    pendingSceneOpen: $pendingSceneOpenRequests,
  },
  filter: ({ latestRequestId, pendingSceneOpen }, message) =>
    isAssistantMessage(message) &&
    !isSceneOpenResponse(pendingSceneOpen, message.event.requestId) &&
    isMatchingRequestId(latestRequestId, message.event.requestId),
  fn: (_source, message) => ({
    message: (() => {
      const event = message.event;
      if (event.type !== "assistant_message") {
        throw new Error("Invariant: expected assistant_message event.");
      }
      return createAssistantMessage(
        normalizeMessageBlocks(event.message),
        event.message.suggestions ?? [],
        event.message.actions ?? [],
        event.message.links ?? [],
        false,
        event.requestId ?? null
      );
    })(),
    status: "rendering" as const,
  }),
  target: assistantMessageAdded,
});

sample({
  clock: assistantStreamMessageReceived,
  source: {
    latestRequestId: $latestRequestId,
    pendingSceneOpen: $pendingSceneOpenRequests,
  },
  filter: ({ latestRequestId, pendingSceneOpen }, message) =>
    isAssistantMessage(message) &&
    !isSceneOpenResponse(pendingSceneOpen, message.event.requestId) &&
    isMatchingRequestId(latestRequestId, message.event.requestId),
  fn: (_source, message) => {
    const event = message.event;
    if (event.type !== "assistant_message") {
      throw new Error("Invariant: expected assistant_message event.");
    }
    return event.message.actions ?? [];
  },
  target: assistantActionsReceived,
});

// Tour from regular (non-scene_open) responses — sceneId from current state.
// scene_open responses route tour through their own handler below, which
// reads sceneId from the SEND-time snapshot (race protection).
sample({
  clock: assistantStreamMessageReceived,
  source: {
    latestRequestId: $latestRequestId,
    pendingSceneOpen: $pendingSceneOpenRequests,
    appState: $appState,
  },
  filter: ({ latestRequestId, pendingSceneOpen, appState }, message) =>
    isAssistantMessage(message) &&
    !isSceneOpenResponse(pendingSceneOpen, message.event.requestId) &&
    isMatchingRequestId(latestRequestId, message.event.requestId) &&
    Array.isArray(message.event.message.tour) &&
    appState.sceneId !== null,
  fn: ({ appState }, message) => {
    const event = message.event;
    if (event.type !== "assistant_message") {
      throw new Error("Invariant: expected assistant_message event.");
    }
    return {
      sceneId: String(appState.sceneId),
      items: event.message.tour ?? [],
    };
  },
  target: tourReceived,
});

// --- scene_open response handler ---
//
// Imperative coordination because we need to dispatch up to four events
// (tourReceived, sceneOpenMessageBuffered OR assistantMessageAdded,
// assistantActionsReceived, sceneOpenResolved) from a single incoming
// message, with logic that depends on three different stores
// (pendingSceneOpen, sceneLoadingState, the payload itself). Splitting
// this into 4 independent samples would duplicate the filter and obscure
// the empty-card flow.

type SceneOpenResponseProcessed = {
  event: AssistantMessageEvent;
  sceneId: string;
  requestId: string;
  sceneReady: boolean;
};

const sceneOpenResponseProcessed = createEvent<SceneOpenResponseProcessed>();

sample({
  clock: assistantStreamMessageReceived,
  source: $pendingSceneOpenRequests,
  filter: (pendingSceneOpen, message) =>
    isAssistantMessage(message) &&
    Boolean(message.event.requestId) &&
    pendingSceneOpen.has(message.event.requestId ?? ""),
  fn: (pendingSceneOpen, message): SceneOpenResponseProcessed => {
    const event = message.event;
    if (event.type !== "assistant_message") {
      throw new Error("Invariant: expected assistant_message event.");
    }
    const requestId = event.requestId ?? "";
    const sceneId = pendingSceneOpen.get(requestId) ?? "";
    // sceneReady is read imperatively in the watch handler below
    // (against the latest $sceneLoadingState value).
    return { event, sceneId, requestId, sceneReady: false };
  },
  target: sceneOpenResponseProcessed,
});

sceneOpenResponseProcessed.watch(({ event, sceneId, requestId }) => {
  const loading = $sceneLoadingState.getState();
  const sceneReady = loading.status === "ready";

  // 1. Tour: always — even on empty card.
  if (Array.isArray(event.message.tour) && sceneId !== "") {
    tourReceived({ sceneId, items: event.message.tour ?? [] });
  }

  // 2. Card: only if non-empty.
  if (!isEmptyAssistantPayload(event.message)) {
    const blocks = normalizeMessageBlocks(event.message);
    const suggestions = event.message.suggestions ?? [];
    const actions = event.message.actions ?? [];
    const links = event.message.links ?? [];

    // Mirror the intro payload into the per-scene cache for non-chat
    // surfaces (e.g. `OverallSceneOverlay`). Independent from the
    // assistantMessageAdded / buffered branch below — both run.
    if (sceneId !== "") {
      sceneOpenIntroReceived({
        sceneId,
        intro: { blocks, suggestions, actions, links },
      });
    }

    if (sceneReady) {
      assistantMessageAdded({
        message: createAssistantMessage(
          blocks,
          suggestions,
          actions,
          links,
          false,
          requestId,
        ),
        status: "rendering",
      });
    } else {
      sceneOpenMessageBuffered({
        blocks,
        suggestions,
        actions,
        links,
        requestId,
      });
    }
  }

  // 3. Actions: always (focus-sight / update-audio-config / etc are
  //    legitimate even when card is empty).
  if (event.message.actions && event.message.actions.length > 0) {
    assistantActionsReceived(event.message.actions);
  }

  // 4. Unblock the chat input.
  sceneOpenResolved({ requestId });
});

// Buffered card flush — fires after scene reveal completes (or
// immediately, if response was late and scene is already ready by the
// time it arrives).
sample({
  clock: sceneLoadingFinished,
  source: $bufferedSceneOpenMessage,
  filter: (buffered) => buffered !== null,
  fn: (buffered) => {
    // Effector filter type-narrowing doesn't propagate to fn in all
    // versions; the filter above guarantees non-null at this point.
    const b = buffered!;
    return {
      message: createAssistantMessage(
        b.blocks,
        b.suggestions,
        b.actions,
        b.links,
        false,
        b.requestId,
      ),
      status: "rendering" as const,
    };
  },
  target: assistantMessageAdded,
});

// Clear the buffer after the flush dispatches above.
sample({
  clock: sceneLoadingFinished,
  source: $bufferedSceneOpenMessage,
  filter: (buffered) => buffered !== null,
  target: sceneOpenMessageFlushed,
});

sample({
  clock: assistantPromptAccepted,
  source: {
    currentSight: $currentSight,
    sights: $sightsStore,
    sceneMarkers: $sdkSceneMarkersGetter,
    sessionId: $assistantSessionId,
    locale: $locale,
    appState: $appState,
  },
  fn: (source, payload): EventBusRequest => {
    const markers = source.sceneMarkers?.() ?? [];
    return {
      sessionId: source.sessionId ?? undefined,
      responseFormat: "markdown",
      context: buildEventBusContext({
        currentSight: source.currentSight,
        markers,
        locale: source.locale,
        sceneId: source.appState.sceneId,
      }),
      event: {
        type: "user_message",
        text: payload.text,
        requestId: payload.requestId,
      },
    };
  },
  target: publishEventBusFx,
});

sample({
  clock: publishEventBusFx.doneData,
  fn: (result) => result.sessionId,
  target: assistantStreamSessionReceived,
});

sample({
  clock: publishEventBusFx.failData,
  source: $locale,
  fn: (locale, error) => {
    const i18n = getMessages(locale);
    return {
      message: createAssistantMessage(
        toParagraphBlocks(
          error instanceof Error
            ? error.message
            : i18n.assistant.assistantUnavailable
        ),
        [],
        []
      ),
      status: "error" as const,
    };
  },
  target: assistantMessageAdded,
});

$assistantMessages.on(assistantMessageAdded, (messages, { message }) => [
  ...messages,
  message,
]);

$assistantStatus.on(assistantMessageAdded, (_, { message, status }) =>
  message.role === "assistant" && message.disableAnimation ? "idle" : status
);

$assistantIsBusy.on(assistantMessageAdded, (isBusy, { message }) =>
  message.role === "assistant" && message.disableAnimation ? false : isBusy
);

$assistantThinkingText.reset(assistantMessageAdded);

$pendingRequestIds.on(assistantMessageAdded, (ids, { message }) => {
  if (message.role !== "assistant" || !message.requestId) return ids;
  const next = new Set(ids);
  next.delete(message.requestId);
  return next;
});

$latestAnimatedAssistantMessageId.on(
  assistantMessageAdded,
  (_, { message }) =>
    message.role === "assistant" && message.disableAnimation ? null : message.id
);

const assistantAnimationFinalized = createEvent();

sample({
  clock: assistantTextAnimationCompleted,
  source: $latestAnimatedAssistantMessageId,
  filter: (latestId, messageId) => latestId === messageId,
  target: assistantAnimationFinalized,
});

$assistantStatus.on(assistantAnimationFinalized, () => "idle");
$assistantIsBusy.on(assistantAnimationFinalized, () => false);
$latestAnimatedAssistantMessageId.on(assistantAnimationFinalized, () => null);
$pendingRequestIds.on(assistantAnimationFinalized, () => new Set());

// --- Stop / Cancel generation ---

type StopPayload = {
  status: AssistantHudStatus;
  pendingIds: string[];
  sessionId: string | null;
};

const assistantStopProcessed = createEvent<StopPayload>();

sample({
  clock: assistantStopRequested,
  source: {
    status: $assistantStatus,
    pendingIds: $pendingRequestIds,
    sessionId: $assistantSessionId,
  },
  fn: ({ status, pendingIds, sessionId }): StopPayload => ({
    status,
    pendingIds: Array.from(pendingIds),
    sessionId,
  }),
  target: assistantStopProcessed,
});

sample({
  clock: assistantStopProcessed,
  filter: ({ status }) => status === "rendering",
  target: assistantAnimationSkipRequested,
});

sample({
  clock: assistantStopProcessed,
  filter: ({ status }) => status === "sending" || status === "awaiting",
  fn: ({ pendingIds, sessionId }) => ({
    requestIds: pendingIds,
    sessionId,
  }),
  target: cancelGenerationFx,
});

$assistantStatus.on(assistantStopProcessed, () => "idle");
$assistantIsBusy.on(assistantStopProcessed, () => false);
$assistantThinkingText.on(assistantStopProcessed, () => null);
$pendingRequestIds.on(assistantStopProcessed, () => new Set());
$latestRequestId.on(assistantStopProcessed, () => null);
$latestAnimatedAssistantMessageId.on(assistantStopProcessed, () => null);

$assistantMessages.on(assistantAnimationSkipRequested, (messages) => {
  const lastAssistantIdx = messages.findLastIndex(
    (m) => m.role === "assistant"
  );
  if (lastAssistantIdx === -1) return messages;
  const msg = messages[lastAssistantIdx];
  if (msg.role !== "assistant" || msg.disableAnimation) return messages;
  const updated = [...messages];
  updated[lastAssistantIdx] = { ...msg, disableAnimation: true };
  return updated;
});

$assistantMessages.on(assistantSkipAllAnimations, (messages) => {
  const hasAnimatable = messages.some(
    (m) => m.role === "assistant" && !m.disableAnimation
  );
  if (!hasAnimatable) return messages;
  return messages.map((m) =>
    m.role === "assistant" && !m.disableAnimation
      ? { ...m, disableAnimation: true }
      : m
  );
});

$latestAnimatedAssistantMessageId.on(assistantSkipAllAnimations, () => null);

const skipAllAnimationsProcessed = sample({
  clock: assistantSkipAllAnimations,
  source: $assistantStatus,
  fn: (status) => status === "rendering",
});

$assistantStatus.on(skipAllAnimationsProcessed, (current, wasRendering) =>
  wasRendering ? "idle" : current
);

$assistantIsBusy.on(skipAllAnimationsProcessed, (current, wasRendering) =>
  wasRendering ? false : current
);

const assistantSuggestionAccepted = createEvent<AssistantSuggestion>();

type SightSuggestion = Extract<AssistantSuggestion, { type: "sight" }>;

const isSightSuggestion = (
  suggestion: AssistantSuggestion
): suggestion is SightSuggestion => suggestion.type === "sight";

const isSightSuggestionWithoutLabel = (
  suggestion: AssistantSuggestion
): suggestion is SightSuggestion => isSightSuggestion(suggestion) && !suggestion.label;

const isSightSuggestionWithLabel = (
  suggestion: AssistantSuggestion
): suggestion is SightSuggestion & { label: string } =>
  isSightSuggestion(suggestion) && Boolean(suggestion.label);

const isMoreSuggestion = (
  suggestion: AssistantSuggestion
): suggestion is Extract<AssistantSuggestion, { type: "more" }> =>
  suggestion.type === "more";

const buildSightPromptText = (
  sight: Sight | null | undefined,
  locale: Locale,
  fallbackLabel: string
): string => {
  const i18n = getMessages(locale);
  const variants = sight?.promptVariants?.filter((variant) => variant.trim());
  if (variants && variants.length > 0) {
    const variant = variants[Math.floor(Math.random() * variants.length)];
    return formatMessage(variant, { name: fallbackLabel });
  }
  return formatMessage(i18n.assistant.sightPrompt, { name: fallbackLabel });
};

sample({
  clock: assistantSuggestionSelected,
  source: $assistantIsBusy,
  filter: (isBusy) => isBusy,
  fn: () => undefined,
  target: assistantStopRequested,
});

sample({
  clock: assistantSuggestionSelected,
  fn: (suggestion) => suggestion,
  target: assistantSuggestionAccepted,
});

sample({
  clock: assistantSuggestionAccepted,
  filter: isSightSuggestionWithoutLabel,
  fn: (suggestion): SightSelectionPayload => ({
    id: suggestion.sightId,
    smooth: true,
    source: "assistant",
  }),
  target: userSelectedSight,
});

sample({
  clock: assistantSuggestionAccepted,
  filter: isSightSuggestionWithLabel,
  fn: (suggestion) => ({
    id: suggestion.sightId,
    smooth: true,
  }),
  target: goToSight,
});

// --- suggestion_click for sight-with-label and more ---

export const suggestionClickAccepted = createEvent<{
  suggestion: SuggestionClickEvent["suggestion"];
  requestId: string;
  userText: string;
}>();

sample({
  clock: assistantSuggestionAccepted,
  source: { locale: $locale },
  filter: (_, suggestion) => isSightSuggestionWithLabel(suggestion),
  fn: (_, suggestion) => {
    const s = suggestion as SightSuggestion & { label: string };
    return {
      suggestion: {
        type: "sight" as const,
        sightId: s.sightId,
        label: s.label,
        suggestionId: s.suggestionId,
      },
      requestId: createRequestId(),
      userText: s.label,
    };
  },
  target: suggestionClickAccepted,
});

sample({
  clock: assistantSuggestionAccepted,
  source: { sights: $sightsStore, locale: $locale },
  filter: (_, suggestion) => isSightSuggestionWithoutLabel(suggestion),
  fn: ({ sights, locale }, suggestion) => {
    const s = suggestion as SightSuggestion;
    const sight = sights[s.sightId];
    const label = sight?.name ?? s.sightId.toString();
    return {
      suggestion: {
        type: "sight" as const,
        sightId: s.sightId,
        label,
        suggestionId: s.suggestionId,
      },
      requestId: createRequestId(),
      userText: buildSightPromptText(sight, locale, label),
    };
  },
  target: suggestionClickAccepted,
});

sample({
  clock: assistantSuggestionAccepted,
  source: { locale: $locale },
  filter: (_, suggestion) => isMoreSuggestion(suggestion),
  fn: ({ locale }, suggestion) => {
    const i18n = getMessages(locale);
    const s = suggestion as Extract<AssistantSuggestion, { type: "more" }>;
    return {
      suggestion: {
        type: "more" as const,
        label: s.label,
        suggestionId: s.suggestionId,
      },
      requestId: createRequestId(),
      userText: s.label || i18n.assistant.continuePrompt,
    };
  },
  target: suggestionClickAccepted,
});

$assistantMessages.on(suggestionClickAccepted, (messages, { userText, requestId }) => [
  ...messages,
  createUserMessage(userText, requestId),
]);
$assistantStatus.on(suggestionClickAccepted, () => "sending");
$assistantIsBusy.on(suggestionClickAccepted, () => true);
$assistantThinkingText.reset(suggestionClickAccepted);
$latestRequestId.on(suggestionClickAccepted, (_, { requestId }) => requestId);
$pendingRequestIds.on(suggestionClickAccepted, (ids, { requestId }) => {
  const next = new Set(ids);
  next.add(requestId);
  return next;
});

sample({
  clock: suggestionClickAccepted,
  source: {
    currentSight: $currentSight,
    sights: $sightsStore,
    sceneMarkers: $sdkSceneMarkersGetter,
    sessionId: $assistantSessionId,
    locale: $locale,
    appState: $appState,
  },
  fn: (source, { suggestion, requestId }): EventBusRequest => {
    const markers = source.sceneMarkers?.() ?? [];
    return {
      sessionId: source.sessionId ?? undefined,
      responseFormat: "markdown",
      context: buildEventBusContext({
        currentSight: source.currentSight,
        markers,
        locale: source.locale,
        sceneId: source.appState.sceneId,
      }),
      event: {
        type: "suggestion_click",
        requestId,
        suggestion,
      },
    };
  },
  target: publishEventBusFx,
});

// Handle enter-poi-artifact suggestion clicks (opens overlay on top of main scene)
sample({
  clock: assistantSuggestionAccepted,
  filter: (s) => s.type === 'enter-poi-artifact',
  fn: (s) => ({ sceneId: (s as { sceneId: string }).sceneId }),
  target: openPoiArtifact,
});

sample({
  clock: userSelectedSight,
  source: { sights: $sightsStore, locale: $locale },
  filter: ({ sights }, payload) =>
    payload.source !== "poi" && payload.source !== "assistant" && Boolean(sights[payload.id]),
  fn: ({ sights, locale }, payload) => {
    const sight = sights[payload.id];
    const label = sight?.name ?? payload.id.toString();
    return buildSightPromptText(sight, locale, label);
  },
  target: assistantPromptSubmittedSilently,
});

export const poiClickRequestGenerated = createEvent<{ id: SightId; requestId: string }>();

sample({
  clock: userSelectedSight,
  filter: (payload) => payload.source === "poi",
  fn: (payload) => ({
    id: payload.id,
    requestId: createRequestId(),
  }),
  target: poiClickRequestGenerated,
});

$latestRequestId.on(poiClickRequestGenerated, (_, { requestId }) => requestId);
$pendingRequestIds.on(poiClickRequestGenerated, (ids, { requestId }) => {
  const next = new Set(ids);
  next.add(requestId);
  return next;
});
$assistantStatus.on(poiClickRequestGenerated, () => "sending");
$assistantIsBusy.on(poiClickRequestGenerated, () => true);

sample({
  clock: poiClickRequestGenerated,
  source: {
    sights: $sightsStore,
    locale: $locale,
    sceneMarkers: $sdkSceneMarkersGetter,
    sessionId: $assistantSessionId,
    appState: $appState,
  },
  fn: (source, { id, requestId }): EventBusRequest => {
    const sight = source.sights[id];
    const label = sight?.name ?? id.toString();
    const markers = source.sceneMarkers?.() ?? [];
    return {
      sessionId: source.sessionId ?? undefined,
      responseFormat: "markdown",
      context: buildEventBusContext({
        currentSight: sight ?? null,
        markers,
        locale: source.locale,
        sceneId: source.appState.sceneId,
      }),
      event: {
        type: "poi_click",
        sightId: id,
        label,
        requestId,
      },
    };
  },
  target: publishEventBusFx,
});

sample({
  clock: assistantPoiClickRequested,
  source: { sights: $sightsStore, locale: $locale },
  fn: ({ sights, locale }, payload) => {
    const sight = sights[payload.id];
    const label = sight?.name ?? payload.id.toString();
    return buildSightPromptText(sight, locale, label);
  },
  target: assistantUserMessageRequested,
});

sample({
  clock: userSelectedSight,
  source: { sights: $sightsStore, locale: $locale, isBusy: $assistantIsBusy },
  filter: ({ sights, isBusy }, payload) =>
    !isBusy && payload.source !== "poi" && Boolean(sights[payload.id]),
  fn: ({ sights, locale }, payload) => {
    const sight = sights[payload.id];
    const label = sight?.name ?? payload.id.toString();
    return buildSightPromptText(sight, locale, label);
  },
  target: assistantPromptSubmitted,
});

assistantActionsReceived.watch((actions) => {
  actions.forEach((action) => {
    if (action.type === "focus-sight") {
      goToSight({ id: action.sightId, smooth: true });
      return;
    }
    if (action.type === "update-audio-config") {
      updateAudioConfig(action.config ?? {});
      return;
    }
    if (action.type === "enter-poi-artifact") {
      openPoiArtifact({ sceneId: action.sceneId });
    }
  });
});
